import { useEffect, useState } from "react";
import { API, useCurrency } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Search, Check, X, Loader2, ChefHat, Package } from "lucide-react";
import { toast } from "sonner";

export default function AdminKitchenMenus() {
  const { symbol } = useCurrency();
  const [kitchens, setKitchens] = useState([]);
  const [selectedKitchenId, setSelectedKitchenId] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [kitchenItemIds, setKitchenItemIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => { fetchKitchens(); fetchAllItems(); }, []);

  const fetchKitchens = async () => {
    try {
      const res = await fetch(`${API}/kitchens`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setKitchens(data);
        if (data.length > 0 && !selectedKitchenId) {
          setSelectedKitchenId(data[0].kitchen_id);
          fetchKitchenMenu(data[0].kitchen_id);
        }
      }
    } catch (e) { console.error(e); }
  };

  const fetchAllItems = async () => {
    try {
      // Use the public menu endpoint to get all items (not filtered by role)
      const res = await fetch(`${API}/menu`, { credentials: "include" });
      if (res.ok) setAllItems(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchKitchenMenu = async (kitchenId) => {
    try {
      const res = await fetch(`${API}/admin/kitchen-menus/${kitchenId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setKitchenItemIds(new Set(data.map((d) => d.item_id)));
      }
    } catch (e) { console.error(e); }
  };

  const handleKitchenChange = (id) => {
    setSelectedKitchenId(id);
    fetchKitchenMenu(id);
  };

  const toggleItem = (itemId) => {
    setKitchenItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const selectAll = () => setKitchenItemIds(new Set(filteredItems.map((i) => i.item_id)));
  const deselectAll = () => setKitchenItemIds(new Set());

  const handleSave = async () => {
    if (!selectedKitchenId) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/kitchen-menus/${selectedKitchenId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ item_ids: [...kitchenItemIds] }),
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail); return; }
      toast.success(`Kitchen menu saved (${kitchenItemIds.size} items)`);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const selectedKitchen = kitchens.find((k) => k.kitchen_id === selectedKitchenId);
  const filteredItems = allItems.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase()) ||
    (item.category || "").toLowerCase().includes(search.toLowerCase())
  );

  const categories = [...new Set(allItems.map((i) => i.category).filter(Boolean))];

  return (
    <AdminLayout>
      <div className="animate-fade-in overflow-x-hidden" data-testid="admin-kitchen-menus">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Kitchen Menus</h1>
            <p className="text-sm text-gray-500 mt-1">Assign menu items from common pool to each kitchen</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <Select value={selectedKitchenId} onValueChange={handleKitchenChange}>
              <SelectTrigger className="w-48" data-testid="kitchen-menu-select">
                <SelectValue placeholder="Select kitchen" />
              </SelectTrigger>
              <SelectContent>
                {kitchens.map((k) => (
                  <SelectItem key={k.kitchen_id} value={k.kitchen_id}>{k.kitchen_code} - {k.city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSave} disabled={saving || !selectedKitchenId} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-kitchen-menu-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
              Save
            </Button>
          </div>
        </div>

        {selectedKitchen && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 flex items-center gap-3">
            <ChefHat size={18} className="text-orange-600" />
            <div>
              <p className="text-sm font-bold text-orange-800">{selectedKitchen.kitchen_code} — {selectedKitchen.city}</p>
              <p className="text-xs text-orange-600">{kitchenItemIds.size} items assigned</p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items..." className="pl-9" data-testid="kitchen-menu-search" />
          </div>
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">Select All</Button>
          <Button variant="outline" size="sm" onClick={deselectAll} data-testid="deselect-all-btn">Clear All</Button>
        </div>

        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
        ) : (
          <div>
            {categories.map((cat) => {
              const catItems = filteredItems.filter((i) => i.category === cat);
              if (catItems.length === 0) return null;
              const selectedCount = catItems.filter((i) => kitchenItemIds.has(i.item_id)).length;
              return (
                <div key={cat} className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">{cat}</h3>
                    <span className="text-[10px] text-gray-500">{selectedCount}/{catItems.length} selected</span>
                  </div>
                  <div className="grid gap-2">
                    {catItems.map((item) => {
                      const isSelected = kitchenItemIds.has(item.item_id);
                      return (
                        <button key={item.item_id} onClick={() => toggleItem(item.item_id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left overflow-hidden ${isSelected ? "border-green-500 bg-green-50" : "border-gray-100 bg-white hover:bg-gray-50"}`}
                          data-testid={`menu-item-toggle-${item.item_id}`}
                        >
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-green-600" : "bg-gray-200"}`}>
                            {isSelected ? <Check size={14} className="text-white" /> : null}
                          </div>
                          {item.image_url && <img src={item.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                              {item.is_vegetarian && <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-bold whitespace-nowrap flex-shrink-0">VEG</span>}
                            </div>
                            <p className="text-[10px] text-gray-400 truncate max-w-full">{item.description}</p>
                          </div>
                          <span className="text-sm font-bold text-gray-900 flex-shrink-0 whitespace-nowrap">{symbol}{Math.round(item.price)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
